import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    X, Loader2, FlaskConical, Layers, Ruler, AlertTriangle, Info, Plus, Trash2,
    Send, ShieldAlert,
} from 'lucide-react';
import {
    getRanges, createTest, updateTest, createRange, updateRange, deleteRange,
    resetTestEditor,
} from '../../redux/labSlice';

// Add / edit an entry in the test master, with its reference bands in the same
// modal. One component for both create and edit: the field set is identical and
// the only differences are the title and the endpoint.
//
// WHY THE REFERENCE RANGES ARE IN HERE AND NOT ON A SEPARATE SCREEN
// A numeric test with no band is not broken in any way the software can see —
// labService.flagFor honestly answers '' and the report prints an empty reference
// column — so every result for that analyte goes out unflagged and nothing on the
// bench announces it. The gap is created at the moment somebody adds the test, so
// it has to be closable at that moment; a second screen behind a second click is
// one that gets visited after the first fifty results have already been reported.
//
// WHAT THIS FORM DECIDES, MONTHS FROM NOW
//  - `containerColour` is what a phlebotomist reaches for at the trolley. The
//    wrong tube does not give a wrong answer, it gives NO answer, and the patient
//    is bled a second time. Hence a swatch picker rather than a text box.
//  - the bands decide which results ring a telephone at 2am. The ordering rules
//    below (critical outside normal) are enforced here as well as on the server,
//    not to save a round trip but because being told "criticalLow must sit at or
//    below low" while looking at both numbers is what makes the rule learnable.
//  - `isPanel` changes what is MEASURED: a panel receives no result row of its
//    own and expands into its components. It is not a display flag.

// ---------------------------------------------------------------------------

/**
 * THE TUBE COLOURS, shared with LabTestMaster.jsx so the dot on the list and the
 * swatch in this form are the same colour for the same stored string. Exported
 * rather than duplicated: two independent lookups drifting apart would mean the
 * master screen showing lavender for a test whose form said grey, and the trolley
 * being told two different things about one tube is the exact failure the colour
 * exists to prevent.
 *
 * `containerColour` is a free STRING in the model on purpose — every supplier
 * names its products differently and a closed enum would silently miss the one
 * this lab actually buys — so anything typed elsewhere still saves. It simply
 * renders with a neutral dot and its own wording.
 *
 * These two exports make react-refresh warn that a file should export only
 * components. That is accepted deliberately. The obvious way to silence it is to
 * copy the palette into LabTestMaster.jsx, which reintroduces exactly the drift
 * this shares away.
 */
export const TUBE_COLOURS = [
    { value: 'Lavender', hex: '#B79FD4', additive: 'K2 / K3 EDTA', use: 'CBC, ESR, HbA1c, blood grouping' },
    { value: 'Pink', hex: '#F0A6C0', additive: 'K2 EDTA', use: 'Blood bank — group, crossmatch, antibody screen' },
    { value: 'Red', hex: '#D64545', additive: 'None (plain)', use: 'Serum — serology, some chemistry' },
    { value: 'Gold', hex: '#D7A05C', additive: 'Clot activator + separator gel', use: 'Serum chemistry, thyroid, lipids' },
    { value: 'Light blue', hex: '#7EB6E8', additive: '3.2% sodium citrate (9:1)', use: 'Coagulation — PT/INR, APTT, D-dimer' },
    { value: 'Green', hex: '#5BAF6A', additive: 'Lithium / sodium heparin', use: 'Plasma chemistry, ammonia, karyotype' },
    { value: 'Grey', hex: '#9AA3A8', additive: 'Sodium fluoride + potassium oxalate', use: 'Glucose, lactate — stops glycolysis' },
    { value: 'Royal blue', hex: '#2A5FA8', additive: 'Trace-element free', use: 'Heavy metals, zinc, copper, selenium' },
    { value: 'Black', hex: '#3A3A3A', additive: 'Sodium citrate (4:1)', use: 'Westergren ESR' },
    { value: 'Yellow', hex: '#E8C34A', additive: 'ACD / SPS', use: 'HLA typing, blood culture, urine container' },
    { value: 'Orange', hex: '#E8862E', additive: 'Thrombin', use: 'Rapid serum — casualty turnaround' },
    { value: 'White', hex: '#EDEFF1', additive: 'None', use: 'Sterile container — urine, stool, sputum, fluid' },
];

/**
 * Stored string (lower-cased and trimmed) to a swatch. Aliases are included
 * because a lab that imported its menu from another system holds 'purple' and
 * 'EDTA' where this one writes 'Lavender', and a colour dot that goes neutral on
 * a perfectly ordinary tube teaches staff to stop trusting the dot.
 */
export const TUBE_SWATCH = {
    ...Object.fromEntries(TUBE_COLOURS.map((c) => [c.value.toLowerCase(), c.hex])),
    purple: '#B79FD4',
    violet: '#B79FD4',
    edta: '#B79FD4',
    'lavender top': '#B79FD4',
    plain: '#D64545',
    sst: '#D7A05C',
    'gold top': '#D7A05C',
    yellow_gold: '#D7A05C',
    blue: '#7EB6E8',
    citrate: '#7EB6E8',
    heparin: '#5BAF6A',
    'grey/black': '#9AA3A8',
    gray: '#9AA3A8',
    fluoride: '#9AA3A8',
    'dark blue': '#2A5FA8',
    'navy blue': '#2A5FA8',
    'sterile container': '#EDEFF1',
    container: '#EDEFF1',
};

const SAMPLE_TYPES = ['blood', 'serum', 'plasma', 'urine', 'stool', 'swab', 'tissue', 'fluid', 'other'];

// Mirrors labTestModel.resultType. Each one changes what the technician is shown
// at entry, so it is a decision about the bench and not about storage.
const RESULT_TYPES = [
    { value: 'numeric', labelKey: 'labtestpage.rt_numeric', fallback: 'Numeric — a measured value with a reference interval' },
    { value: 'text', labelKey: 'labtestpage.rt_text', fallback: 'Text — free wording, e.g. a microscopy description' },
    { value: 'positive_negative', labelKey: 'labtestpage.rt_posneg', fallback: 'Positive / negative' },
    { value: 'options', labelKey: 'labtestpage.rt_options', fallback: 'Options — one of a fixed list' },
];

const GENDERS = ['any', 'male', 'female'];

// 150 years, and EXACTLY the value labService.ageApplies treats as "no upper
// limit". It is also the only upper bound that lets a band cover a patient with
// no date of birth on file — which in a casualty department is a large minority.
// A band written 0–36500 ("100 years, surely enough") silently excludes all of
// them, so this editor writes the sentinel rather than a number somebody chose.
const AGE_MAX_DAYS = 54750;

// Years are 365 days here, not 365.25, because 150 × 365 is exactly AGE_MAX_DAYS.
// With 365.25 a band entered as "0 to 150 years" would land on 54787, miss the
// sentinel by 37 days, and stop covering patients with no DOB — a band that looks
// identical on screen to one that works.
const AGE_UNIT_DAYS = { days: 1, months: 30, years: 365 };

/**
 * The message off a rejected thunk. labSlice's rejectionDetail puts the server's
 * own wording on `error`, and it is worth preferring to anything written here:
 * 'criticalLow must sit at or below low' names the field and the rule, where a
 * house string would say only that the band was refused.
 */
const rejectionText = (err, fallback) =>
    (typeof err?.error === 'string' && err.error) || err?.message || fallback;

/** Days back into the largest unit that divides them exactly, so an edit that
 *  touches nothing else round-trips to the same number it was typed as. */
const daysToParts = (days) => {
    const d = parseInt(days, 10);
    if (!Number.isFinite(d) || d < 0) return { value: '0', unit: 'days' };
    if (d === 0) return { value: '0', unit: 'days' };
    if (d % AGE_UNIT_DAYS.years === 0) return { value: String(d / AGE_UNIT_DAYS.years), unit: 'years' };
    if (d % AGE_UNIT_DAYS.months === 0) return { value: String(d / AGE_UNIT_DAYS.months), unit: 'months' };
    return { value: String(d), unit: 'days' };
};

const partsToDays = (value, unit) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * (AGE_UNIT_DAYS[unit] || 1));
};

const numOrNull = (v) => {
    if (v === null || v === undefined || String(v).trim() === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const looksNumeric = (v) => v === null || v === undefined || String(v).trim() === '' || Number.isFinite(Number(v));

let draftSeq = 0;
const newDraftKey = () => { draftSeq += 1; return `draft-${draftSeq}`; };

/** A blank band. Seeded with the test's own unit — an empty unit on a band is
 *  what makes a µmol/L-versus-mg/dL mismatch invisible at the moment it is
 *  created, and every result for the analyte is then flagged against the wrong
 *  scale. */
const emptyBand = (unit = '') => ({
    key: newDraftKey(),
    rangeId: null,
    gender: 'any',
    ageMinValue: '0',
    ageMinUnit: 'days',
    ageMaxValue: '',
    ageMaxUnit: 'years',
    ageUnbounded: true,
    low: '', high: '', criticalLow: '', criticalHigh: '',
    unit: unit || '',
    textNormal: '',
    note: '',
});

const bandFromServer = (r) => {
    const min = daysToParts(r.ageMinDays);
    const unbounded = (parseInt(r.ageMaxDays, 10) || 0) >= AGE_MAX_DAYS;
    const max = daysToParts(r.ageMaxDays);
    return {
        key: r.rangeId,
        rangeId: r.rangeId,
        gender: r.gender || 'any',
        ageMinValue: min.value,
        ageMinUnit: min.unit,
        ageMaxValue: unbounded ? '' : max.value,
        ageMaxUnit: unbounded ? 'years' : max.unit,
        ageUnbounded: unbounded,
        low: r.low === null || r.low === undefined ? '' : String(r.low),
        high: r.high === null || r.high === undefined ? '' : String(r.high),
        criticalLow: r.criticalLow === null || r.criticalLow === undefined ? '' : String(r.criticalLow),
        criticalHigh: r.criticalHigh === null || r.criticalHigh === undefined ? '' : String(r.criticalHigh),
        unit: r.unit || '',
        textNormal: r.textNormal || '',
        note: r.note || '',
    };
};

const bandPayload = (band, index) => ({
    gender: band.gender,
    ageMinDays: partsToDays(band.ageMinValue, band.ageMinUnit),
    ageMaxDays: band.ageUnbounded ? AGE_MAX_DAYS : partsToDays(band.ageMaxValue, band.ageMaxUnit),
    low: numOrNull(band.low),
    high: numOrNull(band.high),
    criticalLow: numOrNull(band.criticalLow),
    criticalHigh: numOrNull(band.criticalHigh),
    unit: band.unit.trim() || null,
    textNormal: band.textNormal.trim() || null,
    note: band.note.trim() || null,
    // Numbered in tens so a band inserted between two others later does not force
    // the whole list to be renumbered.
    sortOrder: index * 10,
});

/** Two payloads compared as the server stores them, so an edit that changes only
 *  the unit a band is DISPLAYED in (30 days → 1 month) sends no request at all. */
const sameBand = (a, b) => JSON.stringify(a) === JSON.stringify(b);

const EMPTY = {
    code: '', name: '', shortName: '', sectionId: '',
    sampleType: 'blood', containerType: '', containerColour: '',
    method: '', unit: '', loincCode: '',
    price: '', tatHours: '24',
    isPanel: false,
    resultType: 'numeric', options: [], decimals: '2',
    isOutsourced: false, partnerId: '', partnerRate: '',
    preparation: '',
};

export default function AddLabTest({
    isOpen,
    onClose,
    onSaved,
    editing = null,
    sections = [],
    partners = [],
    outsourcingEnabled = false,
}) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [tab, setTab] = useState('details');
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [optionInput, setOptionInput] = useState('');
    // Set only when a CREATE succeeded but the modal stayed open because a
    // reference band was refused. From then on this is an edit, whatever the
    // parent still thinks it handed us. See submit().
    const [savedId, setSavedId] = useState(null);

    const [bands, setBands] = useState([]);
    const [bandsLoading, setBandsLoading] = useState(false);
    // What the server holds right now, keyed by rangeId. The save reconciles
    // against this rather than PUTting every row: an untouched band must not be
    // rewritten, because a write is an audit entry saying somebody changed a
    // reference interval when nobody did.
    const originalBands = useRef({});

    const isEdit = Boolean(editing);
    const testId = editing?.testId || null;

    useEffect(() => {
        if (!isOpen) return;
        setTab('details');
        setErrors({});
        setSaving(false);
        setOptionInput('');
        setSavedId(null);
        // Dropped before the next test's arrive. Leaving them up means somebody
        // can edit one analyte's reference bands while the header says another —
        // and the save would then rewrite the wrong test's intervals.
        setBands([]);
        originalBands.current = {};
        if (editing) {
            setForm({
                code: editing.code || '',
                name: editing.name || '',
                shortName: editing.shortName || '',
                sectionId: editing.sectionId || '',
                sampleType: editing.sampleType || 'blood',
                containerType: editing.containerType || '',
                containerColour: editing.containerColour || '',
                method: editing.method || '',
                unit: editing.unit || '',
                loincCode: editing.loincCode || '',
                price: editing.price === null || editing.price === undefined ? '' : String(editing.price),
                tatHours: String(editing.tatHours || 24),
                isPanel: editing.isPanel === true,
                resultType: editing.resultType || 'numeric',
                options: Array.isArray(editing.options) ? editing.options : [],
                decimals: String(editing.decimals === null || editing.decimals === undefined ? 2 : editing.decimals),
                isOutsourced: editing.isOutsourced === true,
                partnerId: editing.partnerId || '',
                partnerRate: editing.partnerRate === null || editing.partnerRate === undefined ? '' : String(editing.partnerRate),
                preparation: editing.preparation || '',
            });
        } else {
            setForm(EMPTY);
        }
        // The slice keeps one set of bands for the whole module, tagged with the
        // test they belong to. Clearing it here is what stops the previous
        // analyte's intervals being on screen — and therefore savable — while
        // this one's are still in flight.
        dispatch(resetTestEditor());
    }, [isOpen, editing, dispatch]);

    // The bands, for an existing test only. A test that does not exist yet has
    // nothing to hang them on, so those are held as drafts and posted after the
    // create — see submit().
    //
    // The thunk's payload is read straight off `.unwrap()` rather than out of
    // `state.lab.ranges`, because these rows are an editable DRAFT: half-typed
    // bands belong to this form and must not become the module's shared idea of
    // what this test's reference intervals are.
    useEffect(() => {
        if (!isOpen || !testId) return undefined;
        let cancelled = false;
        (async () => {
            setBandsLoading(true);
            try {
                const payload = await dispatch(getRanges({ testId })).unwrap();
                if (cancelled) return;
                const rows = (payload?.data || []).map(bandFromServer);
                setBands(rows);
                // The snapshot is built from the SAME rows the editor holds, and
                // through the same payload builder the save uses. Building it any
                // other way would make an untouched band compare unequal to
                // itself, and every open-and-close of this modal would rewrite
                // every reference interval the test has.
                originalBands.current = Object.fromEntries(
                    rows.map((r, i) => [r.rangeId, bandPayload(r, i)])
                );
            } catch (err) {
                if (cancelled) return;
                setBands([]);
                originalBands.current = {};
                toast.warn(rejectionText(err, t('labtestpage.ranges_load_error', 'Could not load this test’s reference ranges')));
            } finally {
                if (!cancelled) setBandsLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, testId]);

    const set = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const setBand = useCallback((key, patch) => {
        setBands((rows) => rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    }, []);

    const addOption = () => {
        const v = optionInput.trim();
        if (!v) return;
        // Case-insensitive de-dupe, matching normaliseOptions on the server. The
        // whole point of a fixed list is that a growth report cannot hold 'mod',
        // 'Moderate' and 'moderate' as three different findings — and a list that
        // itself holds two spellings reintroduces exactly that.
        if (form.options.some((o) => o.toLowerCase() === v.toLowerCase())) { setOptionInput(''); return; }
        setForm((f) => ({ ...f, options: [...f.options, v] }));
        setOptionInput('');
        setErrors((e) => ({ ...e, options: undefined }));
    };

    const activeSections = useMemo(
        // A closed bench is not offered, except the one this test already sits at:
        // dropping it would silently move the test to "no section" on an ordinary
        // price edit, and a test with no section never appears on a worklist.
        () => (sections || []).filter((s) => s.status === 'active' || s.sectionId === editing?.sectionId),
        [sections, editing]
    );
    const activePartners = useMemo(
        () => (partners || []).filter((p) => p.status === 'active' || p.partnerId === editing?.partnerId),
        [partners, editing]
    );

    const swatchFor = (colour) => TUBE_SWATCH[String(colour || '').trim().toLowerCase()] || null;
    const selectedTube = TUBE_COLOURS.find(
        (c) => c.value.toLowerCase() === String(form.containerColour || '').trim().toLowerCase()
    ) || null;

    // ---- validation ---------------------------------------------------------

    /**
     * The band rules, mirrored from labMasterController.validateRange.
     *
     * THE ORDERING RULES ARE NOT PEDANTRY. labService.flagFor tests the critical
     * bounds FIRST, so a criticalLow sitting above the normal low makes every
     * perfectly normal value 'critical_low' — a telephone call to a clinician for
     * each one, until the bench stops believing the alerts entirely. Checked here
     * as well as on the server so the person is told while both numbers are in
     * front of them.
     */
    const bandErrors = useMemo(() => {
        const out = {};
        bands.forEach((b) => {
            const e = [];
            const ageMin = partsToDays(b.ageMinValue, b.ageMinUnit);
            const ageMax = b.ageUnbounded ? AGE_MAX_DAYS : partsToDays(b.ageMaxValue, b.ageMaxUnit);
            if (!b.ageUnbounded && String(b.ageMaxValue).trim() === '') {
                e.push(t('labtestpage.band_age_max_required', 'Give an upper age, or tick “no upper limit”.'));
            } else if (ageMax < ageMin) {
                e.push(t('labtestpage.band_age_order', 'The upper age is below the lower one — this band would match nobody.'));
            }
            ['low', 'high', 'criticalLow', 'criticalHigh'].forEach((f) => {
                if (!looksNumeric(b[f])) {
                    e.push(t('labtestpage.band_not_a_number', '{{field}} must be a number, or left empty for “unbounded at this end”.', { field: f }));
                }
            });
            const low = numOrNull(b.low);
            const high = numOrNull(b.high);
            const cLow = numOrNull(b.criticalLow);
            const cHigh = numOrNull(b.criticalHigh);
            if (low !== null && high !== null && low > high) {
                e.push(t('labtestpage.band_low_above_high', 'Low cannot be above high.'));
            }
            if (cLow !== null && cHigh !== null && cLow > cHigh) {
                e.push(t('labtestpage.band_clow_above_chigh', 'Critical low cannot be above critical high.'));
            }
            if (cLow !== null && low !== null && cLow > low) {
                e.push(t('labtestpage.band_clow_inside', 'Critical low must sit at or below low — a panic threshold inside the normal interval flags every healthy patient as critical.'));
            }
            if (cHigh !== null && high !== null && cHigh < high) {
                e.push(t('labtestpage.band_chigh_inside', 'Critical high must sit at or above high — a panic threshold inside the normal interval flags every healthy patient as critical.'));
            }
            const hasNumeric = low !== null || high !== null || cLow !== null || cHigh !== null;
            const hasText = Boolean(b.textNormal.trim());
            if (!hasNumeric && !hasText) {
                // A band with no bounds and no expected wording can never change a
                // flag. It looks like configuration, satisfies "this test has a
                // range", and leaves every result unflagged.
                e.push(t('labtestpage.band_inert', 'A band needs at least one bound, or an expected wording — otherwise it can never flag anything.'));
            }
            if (e.length) out[b.key] = e;
        });
        return out;
    }, [bands, t]);

    // Legal, and almost certainly not what the person meant. Said out loud
    // instead of blocking, because a lab that means it must still be able to save.
    const bandWarnings = useMemo(() => {
        const out = {};
        bands.forEach((b) => {
            const w = [];
            const ageMin = partsToDays(b.ageMinValue, b.ageMinUnit);
            const ageMax = b.ageUnbounded ? AGE_MAX_DAYS : partsToDays(b.ageMaxValue, b.ageMaxUnit);
            if (ageMin === 0 && ageMax < AGE_MAX_DAYS) {
                w.push(t('labtestpage.band_no_dob', 'An open-ended band should be left with no upper limit. Ending it early means patients with no date of birth on file get no reference range at all.'));
            }
            const bandUnit = b.unit.trim();
            const testUnit = form.unit.trim();
            if (bandUnit && testUnit
                && bandUnit.replace(/\s+/g, '').toLowerCase() !== testUnit.replace(/\s+/g, '').toLowerCase()) {
                w.push(t('labtestpage.band_unit_mismatch', 'This band is quoted in “{{band}}” while the test reports in “{{test}}”. No conversion is applied.', { band: bandUnit, test: testUnit }));
            }
            const hasNumeric = ['low', 'high', 'criticalLow', 'criticalHigh'].some((f) => numOrNull(b[f]) !== null);
            if (form.resultType === 'numeric' && !hasNumeric) {
                w.push(t('labtestpage.band_text_on_numeric', 'This is a numeric test, so an expected wording alone will never flag a result — add a low/high interval.'));
            }
            if (form.resultType !== 'numeric' && !b.textNormal.trim()) {
                w.push(t('labtestpage.band_numeric_on_text', 'This test does not report a number, so its results are compared against the expected wording — the bounds here will never be used.'));
            }
            if (w.length) out[b.key] = w;
        });
        return out;
    }, [bands, form.unit, form.resultType, t]);

    const validate = () => {
        const e = {};
        if (!form.code.trim()) e.code = t('labtestpage.code_required', 'A code is required — it is what a requisition is written against');
        if (!form.name.trim()) e.name = t('labtestpage.name_required', 'A name is required — it is printed on the report line');
        const price = Number(form.price || 0);
        if (!Number.isFinite(price) || price < 0) e.price = t('labtestpage.price_invalid', 'Price cannot be negative');
        const tat = parseInt(form.tatHours, 10);
        if (!Number.isInteger(tat) || tat < 1 || tat > 8760) {
            // A mistyped 240000 hides a lost sample for ever behind a promise
            // nobody can miss, because tatHours is what makes the overdue
            // worklist possible.
            e.tatHours = t('labtestpage.tat_invalid', 'Turnaround must be between 1 and 8760 hours (one year)');
        }
        const dec = parseInt(form.decimals, 10);
        if (!Number.isInteger(dec) || dec < 0 || dec > 6) {
            e.decimals = t('labtestpage.decimals_invalid', 'Decimal places must be between 0 and 6');
        }
        if (form.resultType === 'options' && form.options.length === 0) {
            e.options = t('labtestpage.options_required', 'An options test needs at least one permitted value, or the technician has nothing to pick');
        }
        if (form.isOutsourced && !form.partnerId) {
            e.partnerId = t('labtestpage.partner_required', 'A referred test must name the lab — the report has to disclose who performed it');
        }
        if (form.isOutsourced && form.isPanel) {
            e.isPanel = t('labtestpage.panel_not_outsourced', 'A panel cannot itself be referred out — mark its component tests instead, because the panel never receives a result row');
        }
        if (form.partnerRate !== '' && (!Number.isFinite(Number(form.partnerRate)) || Number(form.partnerRate) < 0)) {
            e.partnerRate = t('labtestpage.rate_invalid', 'The partner rate cannot be negative');
        }
        if (!form.isPanel && Object.keys(bandErrors).length) {
            e.bands = t('labtestpage.bands_invalid', 'Fix the reference ranges below before saving');
        }
        setErrors(e);
        // Land on the pane that holds the problem, and prefer the details pane
        // when both have one — a red message on a tab nobody switched to is the
        // same as no message at all.
        const keys = Object.keys(e);
        if (keys.some((k) => k !== 'bands')) setTab('details');
        else if (e.bands) setTab('ranges');
        return keys.length === 0;
    };

    // ---- save ---------------------------------------------------------------

    const testPayload = () => ({
        code: form.code.trim(),
        name: form.name.trim(),
        shortName: form.shortName.trim() || null,
        sectionId: form.sectionId || null,
        sampleType: form.sampleType,
        containerType: form.containerType.trim() || null,
        containerColour: form.containerColour.trim() || null,
        method: form.method.trim() || null,
        unit: form.unit.trim() || null,
        loincCode: form.loincCode.trim() || null,
        price: form.price === '' ? 0 : Number(form.price),
        tatHours: parseInt(form.tatHours, 10),
        isPanel: form.isPanel,
        resultType: form.resultType,
        options: form.resultType === 'options' ? form.options : [],
        decimals: parseInt(form.decimals, 10),
        isOutsourced: form.isOutsourced,
        // Empty means "no partner", and null is how the server is told to clear
        // one. Sending '' would store a partnerId nothing matches, and the report
        // would name a reference lab that does not exist.
        partnerId: form.partnerId || null,
        partnerRate: form.partnerRate === '' ? 0 : Number(form.partnerRate),
        preparation: form.preparation.trim() || null,
    });

    /**
     * Reconcile the bands against what the server held when the modal opened:
     * post the new ones, put the changed ones, delete the removed ones. There is
     * no replace-all endpoint on purpose — a band is deleted rather than
     * deactivated (every result snapshots the interval it was flagged against),
     * so a blind replace would churn rangeIds that nothing needs churned.
     *
     * Failures are collected rather than thrown. The test itself has already
     * saved by this point, and abandoning the remaining bands because one was
     * refused would leave a half-configured analyte with nothing on screen
     * saying which half.
     *
     * EVERY SUCCESS IS WRITTEN BACK before returning, and that is what makes the
     * retry safe. A band that posted successfully alongside one that was refused
     * keeps `rangeId: null` otherwise — so pressing Save again posts it a SECOND
     * time, and the analyte ends up with two identical intervals fighting over
     * which one flags a patient. Recording the id turns the retry into an update.
     */
    const saveBands = async (id) => {
        const problems = [];
        const warnings = [];
        const keep = new Set();
        const saved = bands.map((b) => ({ ...b }));
        const snapshot = { ...originalBands.current };

        for (let i = 0; i < saved.length; i += 1) {
            const band = saved[i];
            const payload = bandPayload(band, i);
            try {
                if (band.rangeId) {
                    keep.add(band.rangeId);
                    if (sameBand(payload, snapshot[band.rangeId])) continue;
                    const res = await dispatch(updateRange({ testId: id, rangeId: band.rangeId, data: payload })).unwrap();
                    warnings.push(...(res?.warnings || []));
                    snapshot[band.rangeId] = payload;
                } else {
                    const res = await dispatch(createRange({ testId: id, data: payload })).unwrap();
                    warnings.push(...(res?.warnings || []));
                    const rangeId = res?.data?.rangeId;
                    if (rangeId) {
                        band.rangeId = rangeId;
                        band.key = rangeId;
                        keep.add(rangeId);
                        snapshot[rangeId] = payload;
                    }
                }
            } catch (err) {
                problems.push(rejectionText(err, t('labtestpage.band_save_error', 'A reference range could not be saved')));
            }
        }

        for (const rangeId of Object.keys(snapshot)) {
            if (keep.has(rangeId)) continue;
            try {
                const res = await dispatch(deleteRange({ testId: id, rangeId })).unwrap();
                warnings.push(...(res?.warnings || []));
                delete snapshot[rangeId];
            } catch (err) {
                problems.push(rejectionText(err, t('labtestpage.band_delete_error', 'A reference range could not be deleted')));
            }
        }

        originalBands.current = snapshot;
        if (problems.length) setBands(saved);

        return { problems, warnings };
    };

    const submit = async () => {
        if (!validate()) return;

        setSaving(true);
        try {
            // savedId is the create that already succeeded on a previous press.
            // Without it, a band failure that keeps this modal open would make the
            // next press create a SECOND identical test — the retry has to know
            // the test already exists.
            let id = testId || savedId;
            let warnings = [];

            if (id) {
                const res = await dispatch(updateTest({ testId: id, data: testPayload() })).unwrap();
                warnings = res?.warnings || [];
            } else {
                const res = await dispatch(createTest(testPayload())).unwrap();
                id = res?.data?.testId || null;
                setSavedId(id);
                warnings = res?.warnings || [];
            }

            // A panel has no value of its own, so it can hold no band — the server
            // refuses one with RANGE_ON_PANEL. Nothing is posted for it.
            let problems = [];
            if (id && !form.isPanel) {
                const outcome = await saveBands(id);
                warnings = [...warnings, ...outcome.warnings];
                problems = outcome.problems;
            } else if (!id && bands.length) {
                // The test saved but its id did not come back, so there is nothing
                // to hang the bands on. Said out loud rather than swallowed: the
                // alternative is an analyte that silently reports every result
                // unflagged and looks fully configured on this screen.
                toast.warn(t('labtestpage.bands_not_saved', 'The test was saved but its reference ranges were not. Re-open it and add them.'));
            }

            if (problems.length) {
                // The TEST is saved; some of its bands are not. Closing here would
                // report success for a half-configured analyte and throw away the
                // only screen that says which half — so the modal stays open on
                // the pane holding the problem, and the list behind it refreshes
                // because the test itself really does exist now.
                problems.slice(0, 3).forEach((p) => toast.error(p));
                toast.warn(t('labtestpage.bands_partial', 'The test is saved. Its reference ranges are not — fix the ones flagged below and save again.'));
                setTab('ranges');
                onSaved?.();
                return;
            }

            toast.success(isEdit || savedId
                ? t('labtestpage.update_success', 'Test updated')
                : t('labtestpage.create_success', 'Test added to the menu'));
            // Warnings are the interesting half of the answer here — a changed
            // unit leaving every band quoted in the old one, a panel with no
            // components — so they are shown after the success, not instead of it.
            warnings.slice(0, 3).forEach((w) => toast.warn(w));

            onSaved?.();
            onClose?.();
        } catch (err) {
            toast.error(rejectionText(err, t('labtestpage.save_error', 'Failed to save the test')));
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    const field = 'w-full px-3 py-2.5 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';
    const small = 'w-full px-2 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';
    const label = 'text-xs font-medium text-slate-500';

    const bandCount = bands.length;
    const gapWarning = !form.isPanel && form.resultType === 'numeric' && bandCount === 0;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4" data-testid="add-lab-test-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-xl bg-[#4ABDBD]/10 flex items-center justify-center">
                            {form.isPanel
                                ? <Layers className="w-4 h-4 text-[#2D8A8A]" />
                                : <FlaskConical className="w-4 h-4 text-[#2D8A8A]" />}
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-[#1a365d]">
                                {isEdit || savedId ? t('labtestpage.edit_test', 'Edit test') : t('labtestpage.add_test', 'Add test')}
                            </h2>
                        </div>
                    </div>
                    <button type="button" onClick={onClose} data-testid="add-lab-test-close" className="text-gray-400 hover:text-gray-600 cursor-pointer">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Two panes rather than one long scroll. The bands are a table of
                    their own and burying them under twenty fields is how a test
                    ships with none. */}
                <div className="px-5 pt-4 flex gap-2">
                    {[
                        { id: 'details', icon: FlaskConical, label: t('labtestpage.tab_details', 'Test details') },
                        { id: 'ranges', icon: Ruler, label: t('labtestpage.tab_ranges', 'Reference ranges') },
                    ].map((pane) => (
                        <button
                            key={pane.id}
                            type="button"
                            data-testid={`add-lab-test-tab-${pane.id}`}
                            onClick={() => setTab(pane.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors cursor-pointer ${
                                tab === pane.id
                                    ? 'bg-[#4ABDBD]/10 border-[#4ABDBD] text-[#2D8A8A]'
                                    : 'bg-white border-gray-200 text-gray-500 hover:text-[#2D8A8A]'
                            }`}
                        >
                            <pane.icon className="w-4 h-4" />
                            {pane.label}
                            {pane.id === 'ranges' && !form.isPanel && (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                    gapWarning ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                                }`}>
                                    {bandCount}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {tab === 'details' ? (
                    <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className={label}>{t('labtestpage.code', 'Code')} *</label>
                            <input
                                data-testid="add-lab-test-code"
                                value={form.code}
                                onChange={(e) => set('code', e.target.value.toUpperCase().replace(/\s+/g, ''))}
                                maxLength={32}
                                placeholder="CBC"
                                className={`${field} font-mono ${errors.code ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            {errors.code
                                ? <p className="text-xs text-red-500 mt-1">{errors.code}</p>
                                : <p className="text-[11px] text-slate-400 mt-1">{t('labtestpage.code_hint', 'What a paper requisition is written against, and the fastest way to find the test.')}</p>}
                        </div>
                        <div>
                            <label className={label}>{t('labtestpage.name', 'Name')} *</label>
                            <input
                                data-testid="add-lab-test-name"
                                value={form.name}
                                onChange={(e) => set('name', e.target.value)}
                                maxLength={150}
                                placeholder={t('labtestpage.name_placeholder', 'e.g. Complete Blood Count')}
                                className={`${field} ${errors.name ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                        </div>

                        <div>
                            <label className={label}>{t('labtestpage.short_name', 'Short name')}</label>
                            <input
                                data-testid="add-lab-test-short-name"
                                value={form.shortName}
                                onChange={(e) => set('shortName', e.target.value)}
                                maxLength={60}
                                className={`${field} border-gray-300`}
                            />
                            <p className="text-[11px] text-slate-400 mt-1">
                                {t('labtestpage.short_name_hint', 'What fits in a report column when the full name does not.')}
                            </p>
                        </div>
                        <div>
                            <label className={label}>{t('labtestpage.section', 'Section (bench)')}</label>
                            <select
                                data-testid="add-lab-test-section"
                                value={form.sectionId}
                                onChange={(e) => set('sectionId', e.target.value)}
                                className={`tt-select ${field} border-gray-300`}
                            >
                                <option value="">{t('labtestpage.no_section', 'Not assigned to a bench')}</option>
                                {activeSections.map((s) => (
                                    <option key={s.sectionId} value={s.sectionId}>
                                        {s.name}{s.code ? ` · ${s.code}` : ''}
                                    </option>
                                ))}
                            </select>
                            {/* The section is the QUEUE a sample lands in as well as the
                                report's grouping — a test with none appears on no
                                worklist and nobody is told it is waiting. */}
                            <p className="text-[11px] text-slate-400 mt-1">
                                {t('labtestpage.section_hint', 'Which worklist the sample lands on, and how the report is grouped.')}
                            </p>
                        </div>

                        {/* ---- What is drawn, and into what. ---- */}
                        <div className="sm:col-span-2 mt-1 pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-2 mb-1">
                                <h3 className="text-sm font-bold text-[#2D8A8A]">{t('labtestpage.specimen_section', 'Specimen')}</h3>
                                <span className="text-[11px] text-slate-400">
                                    {t('labtestpage.specimen_hint', 'Printed on the collection slip and shown at the trolley.')}
                                </span>
                            </div>
                        </div>

                        <div>
                            <label className={label}>{t('labtestpage.sample_type', 'Sample type')}</label>
                            <select
                                data-testid="add-lab-test-sample-type"
                                value={form.sampleType}
                                onChange={(e) => set('sampleType', e.target.value)}
                                className={`tt-select ${field} border-gray-300 capitalize`}
                            >
                                {SAMPLE_TYPES.map((s) => (
                                    <option key={s} value={s}>{t(`labtestpage.sample_${s}`, s)}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={label}>{t('labtestpage.container_type', 'Container / tube')}</label>
                            <input
                                data-testid="add-lab-test-container-type"
                                value={form.containerType}
                                onChange={(e) => set('containerType', e.target.value)}
                                maxLength={80}
                                placeholder={t('labtestpage.container_placeholder', 'e.g. K2-EDTA 2 ml')}
                                className={`${field} border-gray-300`}
                            />
                        </div>

                        {/* THE CAP COLOUR. Nobody at the trolley reads 'K2-EDTA 2ml';
                            they reach for the lavender cap. A potassium drawn into
                            EDTA is contaminated beyond use and the patient — often a
                            child — is bled again, so this is a swatch and not a text
                            box. Free text still saves, because every supplier names
                            its products differently. */}
                        <div className="sm:col-span-2">
                            <label className={label}>{t('labtestpage.container_colour', 'Cap colour')}</label>
                            <div className="flex flex-wrap gap-2 mt-1">
                                {TUBE_COLOURS.map((c) => {
                                    const selected = selectedTube?.value === c.value;
                                    return (
                                        <button
                                            key={c.value}
                                            type="button"
                                            data-testid={`add-lab-test-colour-${c.value.toLowerCase().replace(/\s+/g, '-')}`}
                                            onClick={() => set('containerColour', selected ? '' : c.value)}
                                            title={`${c.value} — ${c.additive}. ${c.use}`}
                                            className={`flex items-center gap-2 pl-1.5 pr-2.5 py-1.5 rounded-xl border text-xs font-semibold cursor-pointer transition-all ${
                                                selected
                                                    ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#2D8A8A] shadow-sm'
                                                    : 'border-gray-200 text-gray-600 hover:border-[#4ABDBD]/60'
                                            }`}
                                        >
                                            <span
                                                className="w-4 h-4 rounded-full border border-black/15 flex-shrink-0"
                                                style={{ backgroundColor: c.hex }}
                                            />
                                            {t(`labtestpage.tube_${c.value.toLowerCase().replace(/\s+/g, '_')}`, c.value)}
                                        </button>
                                    );
                                })}
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <span
                                    data-testid="add-lab-test-colour-preview"
                                    className="w-5 h-5 rounded-full border border-black/15 flex-shrink-0"
                                    style={{ backgroundColor: swatchFor(form.containerColour) || '#F1F5F9' }}
                                />
                                <input
                                    data-testid="add-lab-test-container-colour"
                                    value={form.containerColour}
                                    onChange={(e) => set('containerColour', e.target.value)}
                                    maxLength={40}
                                    placeholder={t('labtestpage.colour_placeholder', 'or type the colour this lab’s supplier uses')}
                                    className={`${field} border-gray-300`}
                                />
                            </div>
                            {selectedTube && (
                                <p className="text-[11px] text-slate-500 mt-1">
                                    <span className="font-semibold">{selectedTube.additive}</span> — {selectedTube.use}
                                </p>
                            )}
                        </div>

                        <div className="sm:col-span-2">
                            <label className={label}>{t('labtestpage.preparation', 'Patient preparation')}</label>
                            <textarea
                                data-testid="add-lab-test-preparation"
                                value={form.preparation}
                                onChange={(e) => set('preparation', e.target.value)}
                                rows={2}
                                maxLength={500}
                                placeholder={t('labtestpage.preparation_placeholder', 'e.g. 12 hours fasting — water only')}
                                className={`${field} border-gray-300 resize-none`}
                            />
                            {/* Handed to the patient at the moment of ordering. A lipid
                                profile drawn unfasted is not a slightly worse result, it
                                is an unusable one that has already been run and billed. */}
                            <p className="text-[11px] text-slate-400 mt-1">
                                {t('labtestpage.preparation_hint', 'Printed on the order slip. Without it the sample that arrives may be unusable — and that is only discovered after the analyser has run it.')}
                            </p>
                        </div>

                        {/* ---- What comes back, and how it is read. ---- */}
                        <div className="sm:col-span-2 mt-1 pt-4 border-t border-gray-100">
                            <h3 className="text-sm font-bold text-[#2D8A8A] mb-1">{t('labtestpage.result_section', 'Result and reporting')}</h3>
                        </div>

                        <div>
                            <label className={label}>{t('labtestpage.result_type', 'Result type')}</label>
                            <select
                                data-testid="add-lab-test-result-type"
                                value={form.resultType}
                                onChange={(e) => set('resultType', e.target.value)}
                                disabled={form.isPanel}
                                className={`tt-select ${field} ${form.isPanel ? 'bg-gray-50 text-gray-400' : ''} border-gray-300`}
                            >
                                {RESULT_TYPES.map((r) => (
                                    <option key={r.value} value={r.value}>{t(r.labelKey, r.fallback)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={label}>{t('labtestpage.unit', 'Unit')}</label>
                                <input
                                    data-testid="add-lab-test-unit"
                                    value={form.unit}
                                    onChange={(e) => set('unit', e.target.value)}
                                    maxLength={30}
                                    placeholder="mg/dL"
                                    className={`${field} border-gray-300`}
                                />
                            </div>
                            <div>
                                <label className={label}>{t('labtestpage.decimals', 'Decimals')}</label>
                                <input
                                    data-testid="add-lab-test-decimals"
                                    type="number"
                                    min="0"
                                    max="6"
                                    value={form.decimals}
                                    onChange={(e) => set('decimals', e.target.value)}
                                    className={`${field} ${errors.decimals ? 'border-red-400' : 'border-gray-300'}`}
                                />
                                {errors.decimals && <p className="text-xs text-red-500 mt-1">{errors.decimals}</p>}
                            </div>
                        </div>

                        {form.resultType === 'options' && (
                            <div className="sm:col-span-2">
                                <label className={label}>{t('labtestpage.options', 'Permitted values')} *</label>
                                <div className="flex gap-2">
                                    <input
                                        data-testid="add-lab-test-option-input"
                                        value={optionInput}
                                        onChange={(e) => setOptionInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addOption(); } }}
                                        maxLength={60}
                                        placeholder={t('labtestpage.options_placeholder', 'e.g. Scanty — press Enter')}
                                        className={`${field} ${errors.options ? 'border-red-400' : 'border-gray-300'}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={addOption}
                                        data-testid="add-lab-test-option-add"
                                        className="px-4 py-2 text-sm rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold hover:bg-[#4ABDBD]/10 whitespace-nowrap cursor-pointer"
                                    >
                                        {t('labtestpage.add', 'Add')}
                                    </button>
                                </div>
                                {errors.options && <p className="text-xs text-red-500 mt-1">{errors.options}</p>}
                                {form.options.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {form.options.map((o) => (
                                            <span key={o} className="inline-flex items-center gap-1.5 bg-[#4ABDBD]/10 text-[#2D8A8A] px-2.5 py-1 rounded-lg text-xs font-semibold">
                                                {o}
                                                <button
                                                    type="button"
                                                    onClick={() => setForm((f) => ({ ...f, options: f.options.filter((x) => x !== o) }))}
                                                    className="text-[#2D8A8A]/60 hover:text-red-500 cursor-pointer"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <div>
                            <label className={label}>{t('labtestpage.method', 'Method')}</label>
                            <input
                                data-testid="add-lab-test-method"
                                value={form.method}
                                onChange={(e) => set('method', e.target.value)}
                                maxLength={80}
                                placeholder={t('labtestpage.method_placeholder', 'e.g. CLIA, Colorimetric')}
                                className={`${field} border-gray-300`}
                            />
                            {/* A reference range belongs to a METHOD, not to an analyte.
                                When the platform changes the numbers shift, and a result
                                compared against last year's without knowing that reads as
                                a clinical trend which never happened. */}
                            <p className="text-[11px] text-slate-400 mt-1">
                                {t('labtestpage.method_hint', 'Snapshotted onto every result. Changing it means the bands below need reviewing.')}
                            </p>
                        </div>
                        <div>
                            <label className={label}>{t('labtestpage.loinc', 'LOINC code')}</label>
                            <input
                                data-testid="add-lab-test-loinc"
                                value={form.loincCode}
                                onChange={(e) => set('loincCode', e.target.value.trim())}
                                maxLength={20}
                                className={`${field} border-gray-300 font-mono`}
                            />
                            <p className="text-[11px] text-slate-400 mt-1">
                                {t('labtestpage.loinc_hint', 'What lets this result travel — into an ABDM record, a referral hospital, any analytics that has to pool it with another lab’s name for the same measurement.')}
                            </p>
                        </div>

                        {/* ---- What it costs, and how long it takes. ---- */}
                        <div className="sm:col-span-2 mt-1 pt-4 border-t border-gray-100">
                            <h3 className="text-sm font-bold text-[#2D8A8A] mb-1">{t('labtestpage.tariff_section', 'Tariff and turnaround')}</h3>
                        </div>

                        <div>
                            <label className={label}>{t('labtestpage.price', 'Price (₹)')}</label>
                            <input
                                data-testid="add-lab-test-price"
                                type="number"
                                min="0"
                                step="0.01"
                                value={form.price}
                                onChange={(e) => set('price', e.target.value)}
                                className={`${field} ${errors.price ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            {errors.price && <p className="text-xs text-red-500 mt-1">{errors.price}</p>}
                        </div>
                        <div>
                            <label className={label}>{t('labtestpage.tat', 'Turnaround (hours)')}</label>
                            <input
                                data-testid="add-lab-test-tat"
                                type="number"
                                min="1"
                                max="8760"
                                value={form.tatHours}
                                onChange={(e) => set('tatHours', e.target.value)}
                                className={`${field} ${errors.tatHours ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            {errors.tatHours
                                ? <p className="text-xs text-red-500 mt-1">{errors.tatHours}</p>
                                // The promise made to the patient AND the thing that makes
                                // the overdue worklist possible — an unrealistic figure
                                // hides a lost sample behind a deadline nobody can miss.
                                : <p className="text-[11px] text-slate-400 mt-1">{t('labtestpage.tat_hint', 'What the patient is promised, and what puts the sample on the overdue list when it slips.')}</p>}
                        </div>

                        {/* ---- Panel. ---- */}
                        <div className="sm:col-span-2 mt-1 pt-4 border-t border-gray-100">
                            <label className={`flex items-start gap-3 ${form.isOutsourced ? 'opacity-60' : 'cursor-pointer'}`}>
                                <input
                                    data-testid="add-lab-test-is-panel"
                                    type="checkbox"
                                    checked={form.isPanel}
                                    disabled={form.isOutsourced}
                                    onChange={(e) => set('isPanel', e.target.checked)}
                                    className="mt-0.5 w-4 h-4 accent-[#4ABDBD]"
                                />
                                <span>
                                    <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                        <Layers className="w-4 h-4 text-[#2D8A8A]" />
                                        {t('labtestpage.is_panel', 'This is a panel / profile')}
                                    </span>
                                    {/* Not a display flag: a panel receives no result row of
                                        its own and expands into one row per component. */}
                                    <span className="block text-[11px] text-slate-400 mt-0.5">
                                        {t('labtestpage.is_panel_hint', 'A panel is never measured itself — ordering it expands into one result per component test. Its components are set from the test list once it is saved.')}
                                    </span>
                                </span>
                            </label>
                            {errors.isPanel && <p className="text-xs text-red-500 mt-1">{errors.isPanel}</p>}
                            {form.isPanel && (
                                <p className="text-[11px] text-amber-700 mt-2 flex items-start gap-1">
                                    <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    {t('labtestpage.panel_needs_components', 'A panel with no components cannot be ordered — the order is refused in front of the patient. Add its components after saving.')}
                                </p>
                            )}
                        </div>

                        {/* ---- Referral. Only when the hospital bought it. ---- */}
                        {outsourcingEnabled && (
                            <div className="sm:col-span-2 mt-1 pt-4 border-t border-gray-100">
                                <label className={`flex items-start gap-3 ${form.isPanel ? 'opacity-60' : 'cursor-pointer'}`}>
                                    <input
                                        data-testid="add-lab-test-is-outsourced"
                                        type="checkbox"
                                        checked={form.isOutsourced}
                                        disabled={form.isPanel}
                                        onChange={(e) => set('isOutsourced', e.target.checked)}
                                        className="mt-0.5 w-4 h-4 accent-[#4ABDBD]"
                                    />
                                    <span>
                                        <span className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                            <Send className="w-4 h-4 text-[#2D8A8A]" />
                                            {t('labtestpage.is_outsourced', 'Sent to a reference lab')}
                                        </span>
                                        <span className="block text-[11px] text-slate-400 mt-0.5">
                                            {t('labtestpage.is_outsourced_hint', 'The result may be entered without a tube ever being received here — and the report must disclose who performed it.')}
                                        </span>
                                    </span>
                                </label>

                                {form.isOutsourced && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
                                        <div>
                                            <label className={label}>{t('labtestpage.partner', 'Reference lab')} *</label>
                                            <select
                                                data-testid="add-lab-test-partner"
                                                value={form.partnerId}
                                                onChange={(e) => set('partnerId', e.target.value)}
                                                className={`tt-select ${field} ${errors.partnerId ? 'border-red-400' : 'border-gray-300'}`}
                                            >
                                                <option value="">{t('labtestpage.select_partner', 'Select a reference lab')}</option>
                                                {activePartners.map((p) => (
                                                    <option key={p.partnerId} value={p.partnerId}>
                                                        {p.name}{p.nablNo ? ` · NABL ${p.nablNo}` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors.partnerId && <p className="text-xs text-red-500 mt-1">{errors.partnerId}</p>}
                                            {activePartners.length === 0 && (
                                                <p className="text-[11px] text-amber-700 mt-1">
                                                    {t('labtestpage.no_partners', 'No reference lab is set up yet. Add one before referring tests out.')}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className={label}>{t('labtestpage.partner_rate', 'Partner rate (₹)')}</label>
                                            <input
                                                data-testid="add-lab-test-partner-rate"
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={form.partnerRate}
                                                onChange={(e) => set('partnerRate', e.target.value)}
                                                className={`${field} ${errors.partnerRate ? 'border-red-400' : 'border-gray-300'}`}
                                            />
                                            {errors.partnerRate
                                                ? <p className="text-xs text-red-500 mt-1">{errors.partnerRate}</p>
                                                // The cost side of the same line. It is negotiated
                                                // test by test rather than once per partner, so a
                                                // single "referral discount" would misprice every
                                                // test the moment one item is renegotiated.
                                                : <p className="text-[11px] text-slate-400 mt-1">{t('labtestpage.partner_rate_hint', 'What this hospital pays the reference lab. The patient is still charged the price above.')}</p>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="p-5">
                        {form.isPanel ? (
                            <div data-testid="add-lab-test-ranges-panel-note" className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm text-slate-600 flex items-start gap-2">
                                <Info className="w-4 h-4 mt-0.5 text-[#4ABDBD] flex-shrink-0" />
                                {/* A band on a panel would be compared against nothing for
                                    ever — the panel produces no value of its own. */}
                                {t('labtestpage.ranges_on_panel', 'A panel has no value of its own, so it holds no reference range. Put the bands on its component tests instead.')}
                            </div>
                        ) : (
                            <>
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                                    <div>
                                        <h3 className="text-sm font-bold text-[#2D8A8A]">
                                            {t('labtestpage.ranges_title', 'What counts as normal — and what counts as an emergency')}
                                        </h3>
                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                            {t('labtestpage.ranges_subtitle', 'Bands are matched on gender and on age AT COLLECTION, and the most specific one wins. An exact gender beats “any”; a narrower age band beats a wider one.')}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        data-testid="add-lab-test-add-range"
                                        onClick={() => setBands((rows) => [...rows, emptyBand(form.unit)])}
                                        className="flex items-center gap-2 px-3 py-2 rounded-xl border border-[#4ABDBD] text-[#2D8A8A] font-semibold text-sm hover:bg-[#4ABDBD]/10 whitespace-nowrap cursor-pointer"
                                    >
                                        <Plus className="w-4 h-4" />
                                        {t('labtestpage.add_range', 'Add band')}
                                    </button>
                                </div>

                                {errors.bands && (
                                    <p className="text-xs text-red-500 mb-2">{errors.bands}</p>
                                )}

                                {gapWarning && (
                                    <div data-testid="add-lab-test-no-range-warning" className="mb-3 px-3 py-2 rounded-xl bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
                                        <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                        {/* Not an error. flagFor answers '' honestly and the
                                            report prints an empty reference column — which is
                                            precisely why nothing announces the gap later. */}
                                        {t('labtestpage.no_range_warning', 'This numeric test has no band. Its results will be reported with no reference interval and no high/low flag, and nothing on the bench will say so.')}
                                    </div>
                                )}

                                {bandsLoading ? (
                                    <div className="py-8 flex items-center justify-center text-[#4ABDBD] gap-2 text-sm">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {t('labtestpage.loading', 'Loading…')}
                                    </div>
                                ) : bands.length === 0 ? (
                                    <div className="py-8 text-center text-sm text-gray-500">
                                        {t('labtestpage.no_ranges', 'No bands yet. Add one for adults, then narrower ones for children — a neonatal bilirubin band of 0–7 days beats the adult band that also matches.')}
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {bands.map((b, index) => (
                                            <div
                                                key={b.key}
                                                data-testid={`lab-range-row-${index}`}
                                                className={`p-3 rounded-xl border ${bandErrors[b.key] ? 'border-red-300 bg-red-50/40' : 'border-gray-200 bg-slate-50/60'}`}
                                            >
                                                <div className="flex items-start gap-2">
                                                    <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-3">
                                                        <div>
                                                            <label className="text-[11px] font-medium text-slate-500">{t('labtestpage.band_gender', 'Applies to')}</label>
                                                            <select
                                                                data-testid={`lab-range-gender-${index}`}
                                                                value={b.gender}
                                                                onChange={(e) => setBand(b.key, { gender: e.target.value })}
                                                                className={`tt-select ${small} border-gray-300 bg-white`}
                                                            >
                                                                {GENDERS.map((g) => (
                                                                    <option key={g} value={g}>{t(`labtestpage.gender_${g}`, g === 'any' ? 'Any gender' : g)}</option>
                                                                ))}
                                                            </select>
                                                        </div>

                                                        <div>
                                                            <label className="text-[11px] font-medium text-slate-500">{t('labtestpage.band_age_from', 'Age from')}</label>
                                                            <div className="flex gap-1">
                                                                <input
                                                                    data-testid={`lab-range-age-min-${index}`}
                                                                    type="number"
                                                                    min="0"
                                                                    value={b.ageMinValue}
                                                                    onChange={(e) => setBand(b.key, { ageMinValue: e.target.value })}
                                                                    className={`${small} border-gray-300 bg-white`}
                                                                />
                                                                <select
                                                                    data-testid={`lab-range-age-min-unit-${index}`}
                                                                    value={b.ageMinUnit}
                                                                    onChange={(e) => setBand(b.key, { ageMinUnit: e.target.value })}
                                                                    className={`tt-select ${small} border-gray-300 bg-white w-24`}
                                                                >
                                                                    <option value="days">{t('labtestpage.days', 'days')}</option>
                                                                    <option value="months">{t('labtestpage.months', 'months')}</option>
                                                                    <option value="years">{t('labtestpage.years', 'years')}</option>
                                                                </select>
                                                            </div>
                                                        </div>

                                                        <div>
                                                            <label className="text-[11px] font-medium text-slate-500">{t('labtestpage.band_age_to', 'Age to')}</label>
                                                            <div className="flex gap-1">
                                                                <input
                                                                    data-testid={`lab-range-age-max-${index}`}
                                                                    type="number"
                                                                    min="0"
                                                                    value={b.ageUnbounded ? '' : b.ageMaxValue}
                                                                    disabled={b.ageUnbounded}
                                                                    placeholder={b.ageUnbounded ? t('labtestpage.no_limit', 'no limit') : ''}
                                                                    onChange={(e) => setBand(b.key, { ageMaxValue: e.target.value })}
                                                                    className={`${small} border-gray-300 ${b.ageUnbounded ? 'bg-gray-100' : 'bg-white'}`}
                                                                />
                                                                <select
                                                                    data-testid={`lab-range-age-max-unit-${index}`}
                                                                    value={b.ageMaxUnit}
                                                                    disabled={b.ageUnbounded}
                                                                    onChange={(e) => setBand(b.key, { ageMaxUnit: e.target.value })}
                                                                    className={`tt-select ${small} border-gray-300 w-24 ${b.ageUnbounded ? 'bg-gray-100' : 'bg-white'}`}
                                                                >
                                                                    <option value="days">{t('labtestpage.days', 'days')}</option>
                                                                    <option value="months">{t('labtestpage.months', 'months')}</option>
                                                                    <option value="years">{t('labtestpage.years', 'years')}</option>
                                                                </select>
                                                            </div>
                                                            <label className="flex items-center gap-1.5 mt-1 text-[11px] text-slate-500 cursor-pointer">
                                                                <input
                                                                    data-testid={`lab-range-age-unbounded-${index}`}
                                                                    type="checkbox"
                                                                    checked={b.ageUnbounded}
                                                                    onChange={(e) => setBand(b.key, { ageUnbounded: e.target.checked })}
                                                                    className="w-3.5 h-3.5 accent-[#4ABDBD]"
                                                                />
                                                                {t('labtestpage.no_upper_limit', 'No upper limit')}
                                                            </label>
                                                        </div>

                                                        <div>
                                                            <label className="text-[11px] font-medium text-slate-500">{t('labtestpage.band_unit', 'Unit')}</label>
                                                            <input
                                                                data-testid={`lab-range-unit-${index}`}
                                                                value={b.unit}
                                                                onChange={(e) => setBand(b.key, { unit: e.target.value })}
                                                                maxLength={30}
                                                                placeholder={form.unit || 'mg/dL'}
                                                                className={`${small} border-gray-300 bg-white`}
                                                            />
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        data-testid={`lab-range-delete-${index}`}
                                                        onClick={() => setBands((rows) => rows.filter((r) => r.key !== b.key))}
                                                        title={t('labtestpage.delete_range', 'Remove this band')}
                                                        className="mt-5 text-red-500 p-2 rounded-lg bg-white border border-red-100 hover:bg-red-500 hover:text-white transition-colors cursor-pointer flex-shrink-0"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
                                                    <div>
                                                        <label className="text-[11px] font-medium text-slate-500">{t('labtestpage.band_low', 'Normal low')}</label>
                                                        <input
                                                            data-testid={`lab-range-low-${index}`}
                                                            value={b.low}
                                                            onChange={(e) => setBand(b.key, { low: e.target.value })}
                                                            inputMode="decimal"
                                                            className={`${small} border-gray-300 bg-white`}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[11px] font-medium text-slate-500">{t('labtestpage.band_high', 'Normal high')}</label>
                                                        <input
                                                            data-testid={`lab-range-high-${index}`}
                                                            value={b.high}
                                                            onChange={(e) => setBand(b.key, { high: e.target.value })}
                                                            inputMode="decimal"
                                                            className={`${small} border-gray-300 bg-white`}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[11px] font-medium text-rose-600">{t('labtestpage.band_critical_low', 'Critical low')}</label>
                                                        <input
                                                            data-testid={`lab-range-critical-low-${index}`}
                                                            value={b.criticalLow}
                                                            onChange={(e) => setBand(b.key, { criticalLow: e.target.value })}
                                                            inputMode="decimal"
                                                            className={`${small} border-rose-200 bg-white`}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[11px] font-medium text-rose-600">{t('labtestpage.band_critical_high', 'Critical high')}</label>
                                                        <input
                                                            data-testid={`lab-range-critical-high-${index}`}
                                                            value={b.criticalHigh}
                                                            onChange={(e) => setBand(b.key, { criticalHigh: e.target.value })}
                                                            inputMode="decimal"
                                                            className={`${small} border-rose-200 bg-white`}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
                                                    <div>
                                                        <label className="text-[11px] font-medium text-slate-500">{t('labtestpage.band_text_normal', 'Expected wording')}</label>
                                                        <input
                                                            data-testid={`lab-range-text-normal-${index}`}
                                                            value={b.textNormal}
                                                            onChange={(e) => setBand(b.key, { textNormal: e.target.value })}
                                                            maxLength={120}
                                                            placeholder={t('labtestpage.text_normal_placeholder', 'e.g. Negative')}
                                                            className={`${small} border-gray-300 bg-white`}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[11px] font-medium text-slate-500">{t('labtestpage.band_note', 'Note (printed under the range)')}</label>
                                                        <input
                                                            data-testid={`lab-range-note-${index}`}
                                                            value={b.note}
                                                            onChange={(e) => setBand(b.key, { note: e.target.value })}
                                                            maxLength={200}
                                                            className={`${small} border-gray-300 bg-white`}
                                                        />
                                                    </div>
                                                </div>

                                                {/* A critical bound is a telephone call to a clinician
                                                    and a NABL-recorded acknowledgement, so the band
                                                    says out loud that it will make one. */}
                                                {(numOrNull(b.criticalLow) !== null || numOrNull(b.criticalHigh) !== null) && (
                                                    <p className="text-[11px] text-rose-700 mt-2 flex items-start gap-1">
                                                        <ShieldAlert className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                        {t('labtestpage.critical_note', 'A result outside these bounds raises a critical alert the moment it is entered — somebody has to be told, and who was told is recorded.')}
                                                    </p>
                                                )}

                                                {(bandErrors[b.key] || []).map((msg) => (
                                                    <p key={msg} className="text-[11px] text-red-600 mt-1.5 flex items-start gap-1">
                                                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                        {msg}
                                                    </p>
                                                ))}
                                                {!bandErrors[b.key] && (bandWarnings[b.key] || []).map((msg) => (
                                                    <p key={msg} className="text-[11px] text-amber-700 mt-1.5 flex items-start gap-1">
                                                        <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                        {msg}
                                                    </p>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {!isEdit && !savedId && bands.length > 0 && (
                                    <p className="text-[11px] text-slate-400 mt-3">
                                        {t('labtestpage.bands_saved_after', 'These bands are saved immediately after the test itself — a band has nothing to hang on until the test exists.')}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                )}

                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer"
                    >
                        {t('labtestpage.cancel', 'Cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={saving}
                        data-testid="add-lab-test-submit"
                        className="px-4 py-2 text-sm rounded-xl text-white font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] hover:shadow-lg disabled:opacity-60 flex items-center gap-2 cursor-pointer"
                    >
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {isEdit || savedId ? t('labtestpage.save', 'Save changes') : t('labtestpage.create', 'Add test')}
                    </button>
                </div>
            </div>
        </div>
    );
}
